import { useMutation } from "@apollo/client";
import { Alert, Checkbox, Col, Form, Input, message, Row, Select } from "antd";
import React, { useContext, useEffect, useRef, useState } from "react";
import PhoneInput from "react-phone-number-input";
import { DEFAULT_COUNTRY } from "../../../config";

import { UserContext } from "../../../amplify/authenticator-provider";
import { CREATE_LOCATION, UPDATE_LOCATION } from "../../../graphql/mutation";
import useLang from "../../../hooks/useLang";
import { timezones } from "../../../library/constants";
import { hasAdminAccess } from "../../../library/helpers";
import TextInput from "../../../library/textInput";
import { Countries } from "../../../utils/countries";
import { USStates } from "../../../utils/usStates";
import CustomButton from "../../commonComponents/button";

const LocationForm = ({
  editableLocation = null,
  regions,
  setEditableLocation,
  refetch,
  companyId,
  onClose,
  isSubmit,
  setIsSubmit,
  setEditMode,
  setEditModeData,
  form,
}) => {
  const [lang] = useLang();
  // const [form] = Form.useForm();
  const email = useRef("");
  const title = useRef("");
  const addressLine1 = useRef("");
  const postalCode = useRef("");
  const city = useRef("");
  const geoLink = useRef("");
  const website = useRef("");
  const openaiApiKey = useRef("");
  const locationNotes = useRef("");
  const vdpTextRef = useRef(null);
  const [vdpLink, setvdpLink] = useState("");
  const [createLocation, { loading }] = useMutation(CREATE_LOCATION);
  const [updateLocation, { loading: updateLoading }] = useMutation(UPDATE_LOCATION);

  const [error, setError] = useState("");
  const [location, setLocation] = useState(null);
  const [misc, setmisc] = useState({});
  const { user } = useContext(UserContext);
  const [autoResponseCampaign, setautoResponseCampaign] = useState(false);

  useEffect(() => {
    if (editableLocation) {
      const settings = JSON.parse(editableLocation.settings);
      setautoResponseCampaign(settings?.autoResponseCampaign || false);
      setLocation({
        ...editableLocation,
        enableMFA: settings?.MFA_ENABLED || false,
        mfaMethods: settings?.MFA_METHODS || [],
      });
      form.setFieldsValue({
        ...editableLocation,
        servicePhone: settings?.SERVICE_PHONE,
        replyToEmail: settings?.REPLY_TO_EMAIL,
      });
      setvdpLink(editableLocation?.inventoryUrlFormat || "");
    }
  }, [editableLocation]);

  useEffect(() => {
    if (isSubmit) {
      // eslint-disable-next-line no-use-before-define
      handleUpdateClick();
    }
  }, [isSubmit]);

  const updateLocationForm = (field, value) => {
    setLocation(prevState => {
      const newState = { ...prevState };
      newState[field] = value;
      return newState;
    });
  };

  const validateForm = (location, setError) => {
    let requirmentsSatisfied = true;

    if (!location?.timezone || location?.timezone === "") {
      requirmentsSatisfied = false;
      setError(lang.timeZoneSelected);
    }
    if (!location?.regionId || location?.regionId === "") {
      requirmentsSatisfied = false;
      setError(lang.regionSelected);
    }

    if (!location?.locationType || location?.locationType === "") {
      requirmentsSatisfied = false;
      setError(lang.locationSelected);
    }
    if (!location?.title || location?.title === "") {
      requirmentsSatisfied = false;
      setError(lang.locationNameEntered);
    }
    return requirmentsSatisfied;
  };
  const handleUpdateClick = async values => {
    if (validateForm(location, setError)) {
      setError("");
      if (editableLocation) {
        try {
          const resp = await updateLocation({
            variables: {
              ...values,
              ...location,
              locationId: editableLocation.id,
              inventoryUrlFormat: vdpLink,
              autoResponseCampaign,
              isMfaEnabled: location?.enableMFA || false,
              mfaMethods: location?.mfaMethods || [],
            },
          });

          if (resp?.data?.updateCompanyLocation?.statusCode === 200) {
            onClose();
            refetch();
            setEditableLocation(null);
            onClose();
            message.success(resp?.data?.updateCompanyLocation?.message);
            setEditMode(false);
            setEditModeData(null);
          } else {
            message.error(resp?.data?.updateCompanyLocation?.message);
          }
        } catch (err) {
          console.log("Error in updateLocation", err);
        }
      } else {
        try {
          const resp = await createLocation({
            variables: {
              ...values,
              ...location,
              isDefault: false,
              companyId,
              inventoryUrlFormat: vdpLink,
              autoResponseCampaign,
              isMfaEnabled: location?.enableMFA || false,
              mfaMethods: location?.mfaMethods || [],
            },
          });
          if (resp?.data?.createCompanyLocation?.statusCode === 200) {
            onClose();
            refetch();
            setEditableLocation(null);
            onClose();
            message.success(resp?.data?.createCompanyLocation?.message);
          } else {
            message.error(resp?.data?.createCompanyLocation?.message);
          }
        } catch (err) {
          console.log("Error in createLocation", err);
        }
      }
    }
    setIsSubmit(false);
  };
  const handleAddKeyword = keyword => {
    const ctl = document.getElementById("vdpLinkText");
    const startPos = ctl.selectionStart;

    const output = [vdpLink.slice(0, startPos), `${keyword}`, vdpLink.slice(startPos)].join("");
    setvdpLink(output.trim());
    if (vdpTextRef.current) {
      vdpTextRef.current.focus();
    }
  };

  const requiredLabel = label => (
    <span>
      {label}
      <span style={{ color: "red" }}> *</span>
    </span>
  );
  const isAdminAccess = hasAdminAccess();
  return (
    <Form form={form} layout="vertical" onFinish={handleUpdateClick} className="relative">
      {error && <Alert className="w-full py-1" message={error} type="error" banner />}
      <h3 className="mb-5">Location Information</h3>
      <Row gutter={16} style={{ marginBottom: "20px" }}>
        <Col span={6}>
          <span className="label">{requiredLabel(lang.locationName)}</span>
        </Col>
        <Col span={18}>
          <TextInput
            reference={title}
            placeholder="Enter location name"
            value={location?.title}
            require
            className="flex-1"
            labelClass="my-1 font-medium"
            onChange={e => updateLocationForm("title", e.target.value)}
          />
        </Col>
      </Row>
      <Row gutter={16} style={{ marginBottom: "20px" }}>
        <Col span={6}>
          <span className="label">{lang.emailLabel}</span>
        </Col>
        <Col span={18}>
          <TextInput
            reference={email}
            placeholder="Enter email address"
            className="flex-1"
            labelClass="my-1 font-medium"
            value={location?.email}
            onChange={e => updateLocationForm("email", e.target.value)}
          />
        </Col>
      </Row>
      <Row gutter={16} style={{ marginBottom: "20px" }}>
        <Col span={6}>
          <span className="label">Phone</span>
        </Col>
        <Col span={18}>
          <PhoneInput
            international
            withCountryCallingCode
            countryCallingCodeEditable={false}
            defaultCountry={DEFAULT_COUNTRY}
            placeholder={lang.phoneNumber}
            className="phoneInputCss flex-1"
            value={location?.phone}
            onChange={e => updateLocationForm("phone", e)}
          />
        </Col>
      </Row>
      <Row gutter={16} style={{ marginBottom: "20px" }}>
        <Col span={6}>
          <span className="label">{lang.websiteLink}</span>
        </Col>
        <Col span={18}>
          <TextInput
            reference={website}
            placeholder="Enter website URL"
            className="w-full"
            value={location?.website}
            labelClass="my-1 font-medium"
            onChange={e => updateLocationForm("website", e.target.value)}
          />
        </Col>
      </Row>
      <Row gutter={16} style={{ marginBottom: "20px" }}>
        <Col span={6}>
          <span className="label">{requiredLabel("Region")}</span>
        </Col>
        <Col span={18}>
          <Select
            className="w-full"
            placeholder="Select Region"
            value={+location?.regionId}
            onChange={e => updateLocationForm("regionId", e)}
          >
            {regions.map(item => (
              <Select.Option key={item?.id} value={+item?.id}>
                {item.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
      </Row>
      <Row gutter={16} style={{ marginBottom: "20px" }}>
        <Col span={6}>
          <span className="label">{requiredLabel("Timezone")}</span>
        </Col>
        <Col span={18}>
          <Select
            className="w-full"
            placeholder="Select timezone"
            value={location?.timezone}
            onChange={e => updateLocationForm("timezone", e)}
          >
            {timezones.map(timezone => (
              <Select.Option key={timezone.value} value={timezone.value}>
                {timezone.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
      </Row>
      <Row gutter={16} style={{ marginBottom: "20px" }}>
        <Col span={6}>
          <span className="label">{requiredLabel("Location Type")}</span>
        </Col>
        <Col span={18}>
          <Select
            className="w-full"
            placeholder="Select location type"
            value={location?.locationType}
            onChange={e => updateLocationForm("locationType", e)}
          >
            <Select.Option key="Headquarters" value="Headquarters">
              {lang.headquarters}
            </Select.Option>
            <Select.Option key="Corporate_Office" value="Corporate Office">
              {lang.corporateOffice}
            </Select.Option>
            <Select.Option key="store" value="Store">
              {lang.store}
            </Select.Option>
          </Select>
        </Col>
      </Row>
      <Row gutter={16} style={{ marginBottom: "20px" }}>
        <Col span={6}>
          <span className="label">{lang.mapLink}</span>
        </Col>
        <Col span={18}>
          <TextInput
            reference={geoLink}
            placeholder="Enter Location Link"
            value={location?.geoLink}
            className="w-full"
            labelClass="my-1 font-medium"
            onChange={e => updateLocationForm("geoLink", e.target.value)}
          />
        </Col>
      </Row>
      <Row gutter={16} style={{ marginBottom: "20px" }}>
        <Col span={6}>
          <span className="label">VDP Link format</span>
        </Col>
        <Col span={18}>
          <CustomButton
            key="vinNumber"
            className="px-1 labelColor"
            btnText="{vinNumber}"
            type="link"
            handleClick={() => handleAddKeyword("{vinNumber}")}
          />
          <Input
            id="vdpLinkText"
            value={vdpLink}
            onChange={e => setvdpLink(e.target.value)}
            ref={vdpTextRef}
            placeholder="Enter the VDP link format here"
          />
        </Col>
      </Row>
      <h3 className="mb-5">{lang.addressInfo}</h3>
      <Row gutter={16} style={{ marginBottom: "20px" }}>
        <Col span={6}>
          <span className="label">{lang.address}</span>
        </Col>
        <Col span={18}>
          <TextInput
            reference={addressLine1}
            placeholder={lang.address}
            className="w-full"
            labelClass="my-1 font-medium"
            value={location?.addressLine1}
            onChange={e => updateLocationForm("addressLine1", e.target.value)}
          />
        </Col>
      </Row>
      <Row gutter={16} style={{ marginBottom: "20px" }}>
        <Col span={6}>
          <span className="label">{lang.city}</span>
        </Col>
        <Col span={18}>
          <TextInput
            reference={city}
            placeholder={lang.city}
            className="w-full"
            value={location?.city}
            labelClass="my-1 font-medium"
            onChange={e => updateLocationForm("city", e.target.value)}
          />
        </Col>
      </Row>
      <Row gutter={16} style={{ marginBottom: "20px" }}>
        <Col span={6}>
          <span className="label">State</span>
        </Col>
        <Col span={18}>
          <Select
            showSearch
            placeholder={lang.stateLabel}
            className="w-full"
            value={location?.state}
            onChange={e => updateLocationForm("state", e)}
          >
            {USStates.map((el, i) => (
              <Select.Option key={`key__${i}`} value={el.code}>
                {el.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
      </Row>
      <Row gutter={16} style={{ marginBottom: "20px" }}>
        <Col span={6}>
          <span className="label">{lang.postalCode}</span>
        </Col>
        <Col span={18}>
          <TextInput
            reference={postalCode}
            className="w-full"
            placeholder={lang.postalCode}
            value={location?.postalCode}
            labelClass="my-1 font-medium"
            onChange={e => updateLocationForm("postalCode", e.target.value)}
          />
        </Col>
      </Row>
      <Row gutter={16} style={{ marginBottom: "20px" }}>
        <Col span={6}>
          <span className="label">Country</span>
        </Col>
        <Col span={18}>
          <Select
            showSearch
            placeholder={lang.country}
            className="w-full"
            value={location?.country}
            onChange={e => updateLocationForm("country", e)}
          >
            {Countries.map((el, i) => (
              <Select.Option key={`key__${i}`} value={el.name}>
                {el.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
      </Row>
      <Form.Item
        label="Reply to email"
        rules={[{ type: "email", message: "Please enter a valid email" }]}
        name="replyToEmail"
      >
        <Input name="replyToEmail" placeholder="Reply to email" />
      </Form.Item>
      <Checkbox
        name="autoResponseCampaign"
        checked={autoResponseCampaign}
        onChange={e => setautoResponseCampaign(e.target.checked)}
      >
        Enable Auto Response
      </Checkbox>
      <Row gutter={[16, 16]} className="mt-5">
        <Col span={24} className="flex items-center">
          <Checkbox
            name="enableMFA"
            checked={location?.enableMFA || false}
            onChange={e => updateLocationForm("enableMFA", e.target.checked)}
          >
            Enable MFA for Sign-In
          </Checkbox>
        </Col>
      </Row>
      {location?.enableMFA && (
        <Row gutter={[16, 16]}>
          <Col span={6} className="items-center" style={{ paddingTop: "10px" }}>
            <span className="label">MFA Methods</span>
          </Col>
          <Col span={18} className="flex items-center">
            <div style={{ marginTop: "10px" }}>
              <Checkbox
                checked={location?.mfaMethods?.includes("SOFTWARE_TOKEN_MFA")}
                onChange={e => {
                  const updatedMethods = e.target.checked
                    ? [...(location?.mfaMethods || []), "SOFTWARE_TOKEN_MFA"]
                    : location?.mfaMethods?.filter(method => method !== "SOFTWARE_TOKEN_MFA");
                  updateLocationForm("mfaMethods", updatedMethods);
                }}
              >
                Authenticator App
              </Checkbox>
              <Checkbox
                checked={location?.mfaMethods?.includes("EMAIL_MFA")}
                onChange={e => {
                  const updatedMethods = e.target.checked
                    ? [...(location?.mfaMethods || []), "EMAIL_MFA"]
                    : location?.mfaMethods?.filter(method => method !== "EMAIL_MFA");
                  updateLocationForm("mfaMethods", updatedMethods);
                }}
              >
                Email
              </Checkbox>
              <Checkbox
                checked={location?.mfaMethods?.includes("SMS_MFA")}
                onChange={e => {
                  const updatedMethods = e.target.checked
                    ? [...(location?.mfaMethods || []), "SMS_MFA"]
                    : location?.mfaMethods?.filter(method => method !== "SMS_MFA");
                  updateLocationForm("mfaMethods", updatedMethods);
                }}
              >
                SMS
              </Checkbox>
            </div>
          </Col>
        </Row>
      )}
    </Form>
  );
};

export default LocationForm;
